/**
 * A wrapper to handle the Modern pagination style, and infinite scroll.
 *
 * Usage:
 *  {%- if settings.pagination_style == "modern" or settings.pagination_infinite -%}
 *    <script src="{{ 'custom-pagination.js' | asset_url }}" defer="defer"></script>
 *  {%- endif -%}
 *
 *  {%- paginate thing by stuff -%}
 *    <custom-pagination data-infinite-scroll="{{ settings.pagination_infinite }}"
 *      data-pause-infinite-scroll="false">
 *      <ul>
 *        <li class="js-pagination-result">One</li>
 *        <li class="js-pagination-result">Two</li>
 *        <li class="js-pagination-result">Three</li>
 *      </ul>
 *      {%- if paginate.pages > 1 -%}
 *        {% render 'pagination', paginate: paginate, style: settings.pagination_style %}
 *      {%- endif -%}
 *    </custom-pagination>
 *  {%- endpaginate -%}
 */
if (!customElements.get('custom-pagination')) {
  const navigationStorageKey = 'mastoner-pagination-return-state';

  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.init();
      document.addEventListener('on:bfcache:load-restore', this.reload.bind(this));
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      // Gate only a collection whose initial ranked source is pending.
      if (this.closest('[data-mastoner-stock-first-handle][data-stock-first-state="loading"]')) {
        this.stockFirstSettled = this.stockFirstSettled || (() => this.reload());
        document.addEventListener('mastoner:stock-first:settled', this.stockFirstSettled, { once: true });
        return;
      }
      if (window.MastonerCollectionStockFirst?.owns(this)) {
        this.stockFirstPageHideHandler = this.stockFirstPageHideHandler || (() => {
          this.stockFirstRestoreController?.abort();
          this.controller?.abort();
        });
        window.addEventListener('pagehide', this.stockFirstPageHideHandler);
      }
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      this.pagination = this.querySelector('.js-pagination');
      this.loadMoreButton = this.querySelector('.js-pagination-load-more');
      this.results = this.querySelectorAll('.js-pagination-result');
      this.controller = null;
      this.setResultPageUrls(window.location.href, this.results);
      this.loadedPageUrls = this.getLoadedPageUrls();

      // Bind click handlers to pagination results
      this.resultClickHandler = this.resultClickHandler || this.handleResultClick.bind(this);
      this.addEventListener('click', this.resultClickHandler);

      // Bind click handler to load more button click
      this.loadMoreClickHandler = this.loadMoreClickHandler || this.handleLoadMoreClick.bind(this);

      if (this.pagination && this.hasMoreResults() && this.results) {
        if (this.loadMoreButton) this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);

        if (this.infiniteScroll) {
          this.paginationObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.loadMore();

                  // Continue to load results as long as the 'load more' button is visible
                  this.intersectingTimer = setInterval(this.loadMore.bind(this), 1000);
                } else if (this.intersectingTimer) {
                  clearInterval(this.intersectingTimer);
                }
              });
            },
            { rootMargin: `${window.innerHeight * 1.5}px 0px` }
          );

          this.paginationObserver.observe(this.pagination);
        }
      }

      this.restoreNavigationState();
    }

    /**
     * Destroys the component
     */
    destroy() {
      if (this.stockFirstSettled) document.removeEventListener('mastoner:stock-first:settled', this.stockFirstSettled);
      if (this.stockFirstPageHideHandler) window.removeEventListener('pagehide', this.stockFirstPageHideHandler);
      this.stockFirstRestoreController?.abort();
      if (window.MastonerCollectionStockFirst?.owns(this)) this.controller?.abort();
      if (this.intersectingTimer) clearInterval(this.intersectingTimer);
      if (this.paginationObserver) this.paginationObserver.disconnect();
      if (this.loadMoreButton) this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
      this.removeEventListener('click', this.resultClickHandler);
      this.loadMoreClickHandler = null;
    }

    /**
     * Reloads the component
     */
    reload() {
      this.destroy();
      this.init();
    }

    /**
     * Handles click on the load more button
     * @param {object} evt - Event object
     */
    handleLoadMoreClick(evt) {
      evt.preventDefault();
      this.loadMore();
    }

    /**
     * Handles a click on a item in the pagination
     * @param {object} evt - Event object
     */
    handleResultClick(evt) {
      if (evt.defaultPrevented || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;

      const link = evt.target.closest('a[href]');
      const result = evt.target.closest('.js-pagination-result');

      if (link && result && CustomPagination.isProductLink(link)) {
        this.saveNavigationState(link, result);
        this.replaceUrl(result.dataset.paginationPageUrl);
      }

      if (link && result && this.controller) {
        this.controller.abort();
      }
    }

    /**
     * Fetches more results from the next page of results, and dynamically updates this page
     */
    async loadMore() {
      if (!this.isFetching && this.dataset.pauseInfiniteScroll === 'false') {
        this.isFetching = true;
        this.controller = new AbortController();
        const pageUrl = this.loadMoreButton?.href;

        try {
          // Add the loading state to the button
          if (!pageUrl) throw new Error("No 'Load more' button found.");

          this.loadMoreButton.classList.add('is-loading');
          this.loadMoreButton.setAttribute('aria-disabled', 'true');

          if (this.pagination.dataset.paginationStyle === 'traditional') {
            this.pagination.classList.add('is-loading');
          }

          await this.appendPage(pageUrl, { signal: this.controller.signal, updateHistory: true });
        } catch (error) {
          console.log('Unable to fetch next page of results', error); // eslint-disable-line
          if (this.intersectingTimer) clearInterval(this.intersectingTimer);
        } finally {
          this.controller = null;

          // Remove the loading state from the load more button
          if (this.loadMoreButton) {
            this.loadMoreButton.classList.remove('is-loading');
            this.loadMoreButton.removeAttribute('aria-disabled');
          }

          this.isFetching = false;

          if (this.pagination?.dataset.paginationStyle === 'traditional') {
            this.pagination.classList.remove('is-loading');
          }
        }
      }
    }

    async appendPage(pageUrl, options = {}) {
      const source = window.MastonerCollectionStockFirst;
      const ranked = source?.owns(this);
      const startedHref = window.location.href;
      const response = ranked
        ? await source.fetchPage(this, pageUrl, options)
        : await fetch(pageUrl, { signal: options.signal });

      if (!response.ok) throw new Error(response.status);
      if (this.dataset.pauseInfiniteScroll === 'true' && !options.restore) {
        throw new Error('Infinite scroll was interrupted.');
      }

      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();
      if (ranked && (options.signal?.aborted || !this.isConnected || window.location.href !== startedHref)) {
        throw new Error('Stale collection pagination response.');
      }

      let newResultsHtml = '';
      const lastResult = this.results[this.results.length - 1];
      tmpl.content.querySelectorAll('.js-pagination-result').forEach((result) => {
        result.dataset.paginationPageUrl = pageUrl;
        newResultsHtml += result.outerHTML;
      });

      if (lastResult && newResultsHtml) {
        lastResult.insertAdjacentHTML('afterend', newResultsHtml);
        source?.orderResults(this);
        this.results = this.querySelectorAll('.js-pagination-result');
        this.loadedPageUrls = this.getLoadedPageUrls();
      }

      const newPagination = tmpl.content.querySelector('.js-pagination');
      if (this.pagination && newPagination && newPagination.dataset.isMoreResults === 'true') {
        this.pagination.dataset.isMoreResults = newPagination.dataset.isMoreResults;
        this.pagination.dataset.paginationStyle = newPagination.dataset.paginationStyle;
        this.pagination.innerHTML = newPagination.innerHTML;
        this.rebindLoadMoreButton();
      } else if (this.pagination) {
        this.pagination.remove();
        this.pagination = null;
        this.loadMoreButton = null;
        if (this.intersectingTimer) clearInterval(this.intersectingTimer);
        if (this.paginationObserver) this.paginationObserver.disconnect();
        document.querySelectorAll('.js-when-paginated-only').forEach((elem) => elem.remove());
      }

      if (options.updateHistory) {
        this.replaceUrl(pageUrl);
      }
    }

    hasMoreResults() {
      return this.pagination?.dataset.isMoreResults === 'true';
    }

    rebindLoadMoreButton() {
      if (this.loadMoreButton) {
        this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
      }

      this.loadMoreButton = this.querySelector('.js-pagination-load-more');
      if (this.loadMoreButton) {
        this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
      }
    }

    setResultPageUrls(pageUrl, results) {
      results.forEach((result) => {
        if (!result.dataset.paginationPageUrl) {
          result.dataset.paginationPageUrl = pageUrl;
        }
      });
    }

    getLoadedPageUrls() {
      return [...new Set(
        Array.from(this.results)
          .map((result) => result.dataset.paginationPageUrl)
          .filter(Boolean)
      )];
    }

    saveNavigationState(link, result) {
      const resultPageUrl = result.dataset.paginationPageUrl || window.location.href;
      const state = {
        listUrl: resultPageUrl,
        productPath: CustomPagination.normalizedPath(link.href),
        resultIndex: Array.from(this.results).indexOf(result),
        resultPageUrl,
        loadedPageUrls: this.getLoadedPageUrls(),
        scrollY: window.scrollY,
        timestamp: Date.now()
      };

      try {
        sessionStorage.setItem(navigationStorageKey, JSON.stringify(state));
      } catch (error) {
        void error;
      }
    }

    async restoreNavigationState() {
      const ranked = window.MastonerCollectionStockFirst?.owns(this);
      if (ranked && this.closest('[data-stock-first-state="loading"]')) return;
      const state = CustomPagination.readNavigationState();
      if (!state || !CustomPagination.isSameList(window.location.href, state.listUrl)) return;

      const previousPauseState = this.dataset.pauseInfiniteScroll;
      const restoreController = ranked ? new AbortController() : null;
      if (ranked) {
        this.stockFirstRestoreController?.abort();
        this.stockFirstRestoreController = restoreController;
      }
      this.dataset.pauseInfiniteScroll = 'true';

      try {
        await this.restorePagesForState(state, restoreController?.signal);
        if (restoreController?.signal.aborted || (ranked && !this.isConnected)) return;
        const target = this.findStoredResult(state);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (restoreController?.signal.aborted || (ranked && !this.isConnected)) return;
            if (target) {
              this.scrollToResult(target);
            } else if (Number.isFinite(state.scrollY)) {
              window.scrollTo({ top: state.scrollY, behavior: 'auto' });
            }
            CustomPagination.clearNavigationState();
          });
        });
      } catch (error) {
        // Retain the return state for a later successful restore.
        if (!ranked) throw error;
      } finally {
        if (!ranked || this.stockFirstRestoreController === restoreController) {
          this.dataset.pauseInfiniteScroll = previousPauseState || 'false';
        }
      }
    }

    async restorePagesForState(state, signal) {
      const pageUrls = [...new Set([
        ...(Array.isArray(state.loadedPageUrls) ? state.loadedPageUrls : []),
        state.resultPageUrl
      ].filter(Boolean))];

      for (const pageUrl of pageUrls) {
        if (signal?.aborted) return;
        if (!signal && this.findStoredResult(state)) return;
        if (signal && this.getLoadedPageUrls().some((loaded) => CustomPagination.normalizedUrl(loaded) === CustomPagination.normalizedUrl(pageUrl))) continue;
        if (CustomPagination.normalizedUrl(pageUrl) === CustomPagination.normalizedUrl(window.location.href)) continue;
        await this.appendPage(pageUrl, { restore: true, signal });
      }
    }

    findStoredResult(state) {
      const productPath = state.productPath;
      if (productPath) {
        const result = Array.from(this.results).find((item) => (
          Array.from(item.querySelectorAll('a[href]')).some((link) => CustomPagination.normalizedPath(link.href) === productPath)
        ));
        if (result) return result;
        if (window.MastonerCollectionStockFirst?.owns(this)) return null;
      }

      if (Number.isInteger(state.resultIndex)) {
        return this.results[state.resultIndex] || null;
      }

      return null;
    }

    scrollToResult(result) {
      const headerHeight = Number.parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height').replace('px', ''),
        10
      ) || 0;
      const top = result.getBoundingClientRect().top + window.scrollY - headerHeight - 24;
      window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    }

    replaceUrl(pageUrl) {
      if (!pageUrl) return;

      const url = new URL(pageUrl, window.location.origin);
      const nextPath = `${url.pathname}${url.search}${url.hash}`;
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (nextPath === currentPath) return;

      window.history.replaceState(
        { ...(window.history.state || {}), paginationPageUrl: url.href },
        '',
        nextPath
      );
    }

    static readNavigationState() {
      try {
        const state = JSON.parse(sessionStorage.getItem(navigationStorageKey) || 'null');
        if (!state || Date.now() - state.timestamp > 30 * 60 * 1000) {
          CustomPagination.clearNavigationState();
          return null;
        }
        return state;
      } catch (error) {
        CustomPagination.clearNavigationState();
        return null;
      }
    }

    static clearNavigationState() {
      try {
        sessionStorage.removeItem(navigationStorageKey);
      } catch (error) {
        void error;
      }
    }

    static isProductLink(link) {
      try {
        const url = new URL(link.href, window.location.origin);
        return url.origin === window.location.origin && url.pathname.includes('/products/');
      } catch (error) {
        return false;
      }
    }

    static isSameList(currentUrl, storedUrl) {
      return CustomPagination.listIdentity(currentUrl) === CustomPagination.listIdentity(storedUrl);
    }

    static listIdentity(value) {
      const url = new URL(value, window.location.origin);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      return `${url.pathname}?${url.searchParams.toString()}`;
    }

    static normalizedUrl(value) {
      const url = new URL(value, window.location.origin);
      return `${url.pathname}${url.search}`;
    }

    static normalizedPath(value) {
      const url = new URL(value, window.location.origin);
      return url.pathname.replace(/\/$/, '');
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
